import { useEffect, useState, useRef } from 'react';

import { useVehicleSearchParams } from './usevehiclesearchparams';
import { getVehicleDetails } from '@services/index';
import { useAppDispatch, useAppSelector } from '@hooks/statehooks';
import { updateVehicleInformation } from '@store/reducers/vehicleinformation';
import { updatePrice } from '@store/reducers/vehicleprice';
import { updateVehicleConfiguration } from '@store/reducers/vehicleconfiguration';
import { Vehicle } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicle';
import { VehicleQuickDetails } from 'interface';
import { VehiclePricing } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehiclepricing';
import { VehicleConfiguration } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleconfiguration';
import { useSelectedOptions } from '@hooks/useselectedoptions';
import { AvailableOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/availableoption';
import { PricingType } from '../enums';

export const useVehicle = () => {
  const vehicleInformation = useAppSelector(
    (state) => state.vehicleInformation
  );
  const { pricingType } = useAppSelector((state) => state.vehicleInformation);
  const [vehicleDetails, setVehicleDetails] = useState<Vehicle | object>(
    vehicleInformation
  );

  const { vehicleSearchParams } = useVehicleSearchParams();
  const [loading, setLoading] = useState(true);
  const [quoteValid, setQuoteValid] = useState(false);
  const dispatch = useAppDispatch();
  const hasFetchedData = useRef(false);

  const {
    id,
    duration,
    include_maintenance: includeMaintenance,
    yearly_mileage: yearlyMileage,
    initial_payment: initialPayment,
    optionsSelected,
  } = vehicleSearchParams;

  const { selectOption } = useSelectedOptions();

  //this will check if there is only one option available and select it
  const checkAndSetOption = (options: AvailableOption[]) => {
    if (options.length === 1 && !options.some((x) => x.checked)) {
      options[0].checked = true;
      selectOption(options[0].id);
    }
  };

  //this will check the available options and set one for each type
  //if there is only one available option to select
  const setSelectedOptions = (customisationOptions: VehicleConfiguration) => {
    checkAndSetOption(customisationOptions.colours.paint);
    checkAndSetOption(customisationOptions.colours.trim);
    checkAndSetOption(customisationOptions.colours.hood);

    dispatch(updateVehicleConfiguration(customisationOptions));
  };

  const getVehicleInfo = (
    callback: Function | null = null,
    pricingTypeParam: PricingType | null = null
  ) => {
    getVehicleDetails(
      id,
      duration,
      includeMaintenance,
      initialPayment,
      yearlyMileage,
      optionsSelected,
      null,
      pricingTypeParam ? pricingTypeParam : pricingType
    ).subscribe({
      next: (res: Vehicle) => {
        setVehicleDetails(res);
        dispatch(updateVehicleInformation(res as VehicleQuickDetails));
        dispatch(
          updatePrice({
            id: res.id,
            pricing: res.pricing,
            initialPayment: res.initialPayment,
          } as VehiclePricing)
        );
        const customisationOptions = JSON.parse(
          JSON.stringify(res.customisationOptions)
        ) as VehicleConfiguration;
        setSelectedOptions(customisationOptions);
        if (callback) {
          callback();
        }
        setQuoteValid(res.quoteValid as boolean);
        setLoading(false);
      },
      error: () => {
        setVehicleDetails(Object);
        setLoading(false);
      },
    });
  };

  const getCustomisationValid = (
    pricingTypeParam: PricingType | null = null,
    customisationOptions: number[] | null = null
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      getVehicleDetails(
        id,
        duration,
        includeMaintenance,
        initialPayment,
        yearlyMileage,
        customisationOptions,
        null,
        pricingTypeParam ? pricingTypeParam : pricingType
      ).subscribe({
        next: (res: Vehicle) => {
          resolve(res.customisationOptions?.valid === true ? true : false);
        },
        error: () => {
          resolve(false);
        },
      });
    });
  };

  useEffect(() => {
    if (!hasFetchedData.current && !(vehicleDetails as Vehicle).id) {
      getVehicleInfo();
    } else {
      setLoading(false);
      // this is only required to be checked on page load
      setQuoteValid(true);
    }
  }, [id, duration, includeMaintenance, yearlyMileage, initialPayment]);

  const checkColourOption = (options?: AvailableOption[]) =>
    options && options.length > 1;

  const canCustomise = () => {
    if (!loading) {
      const vehicle = vehicleDetails as Vehicle;

      return (
        checkColourOption(vehicle.customisationOptions?.colours.hood) ||
        checkColourOption(vehicle.customisationOptions?.colours.paint) ||
        checkColourOption(vehicle.customisationOptions?.colours.trim) ||
        (vehicle.customisationOptions?.options &&
          vehicle.customisationOptions.options.some((s) =>
            s.items.some((i) => !i.disabled)
          ))
      );
    }

    return false;
  };

  const getFuelType = () => {
    var fuelType = '';
    switch (vehicleInformation.fuel) {
      case 'P':
        fuelType = 'Petrol';
        break;
      case 'D':
        fuelType = 'Diesel';
        break;
      case 'E':
        fuelType = 'Electric';
        break;
      case 'H':
        fuelType = 'Hybrid';
        break;
      case 'B':
        fuelType = 'Bi-Fuel';
        break;
      case 'X':
        fuelType = 'Plug-in Hybrid';
        break;
    }
    return fuelType;
  };

  return {
    loading,
    vehicleDetails,
    quoteValid,
    canCustomise,
    getVehicleInfo,
    getFuelType,
    getCustomisationValid,
  };
};
